import React from 'react';
import { addDays, format, parseISO, setYear } from 'date-fns';
import { BuildingIcon, ClockIcon, PartyPopperIcon, PlaneIcon, UserPlusIcon, UsersIcon } from 'lucide-react';
import { KpiTile } from '../components/dashboard/KpiTile';
import { PendingRequestsCard } from '../components/dashboard/PendingRequestsCard';
import { WhosOutCard } from '../components/dashboard/WhosOutCard';
import { Avatar } from '../components/ui/Avatar';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { departmentNames } from '../data/org';
import { timeOffEntries } from '../data/timeOff';
import { useHRStore } from '../hooks/useHRStore';
import type { Navigate, PtoRequest } from '../types/hr';
import { displayName } from '../utils/employees';
import { TODAY, formatDate, relativeDay } from '../utils/format';
import { isOutOn } from '../utils/timeOff';
interface DashboardProps {
  requests: PtoRequest[];
  onDecide: (id: string, status: 'approved' | 'denied') => void;
  onNavigate: Navigate;
}
const TODAY_ISO = format(TODAY, 'yyyy-MM-dd');
const MONTH_PREFIX = format(TODAY, 'yyyy-MM');
export function Dashboard({
  requests,
  onDecide,
  onNavigate
}: DashboardProps) {
  const {
    employees
  } = useHRStore();
  const headcount = employees.length;
  const pending = requests.filter(r => r.status === 'pending');
  const oldestPending = pending.reduce<string | null>((oldest, r) => !oldest || r.submitted < oldest ? r.submitted : oldest, null);
  const outToday = timeOffEntries.filter(e => isOutOn(e, TODAY));
  const backTomorrow = outToday.filter(e => e.end === TODAY_ISO).length;
  const newHires = employees.filter(e => e.hireDate.startsWith(MONTH_PREFIX)).sort((a, b) => a.hireDate.localeCompare(b.hireDate));
  const started = newHires.filter(e => e.hireDate <= TODAY_ISO).length;
  const anniversaries = employees.filter(e => e.status !== 'Terminated').map(e => {
    const hire = parseISO(e.hireDate);
    return {
      employee: e,
      date: setYear(hire, TODAY.getFullYear()),
      years: TODAY.getFullYear() - hire.getFullYear()
    };
  }).filter(a => a.years >= 1 && a.date >= TODAY && a.date <= addDays(TODAY, 30)).sort((a, b) => a.date.getTime() - b.date.getTime());
  const departments = departmentNames.map(name => ({
    name,
    headcount: employees.filter(e => e.department === name).length
  })).filter(d => d.headcount > 0).sort((a, b) => b.headcount - a.headcount);
  const maxDept = Math.max(1, ...departments.map(d => d.headcount));
  return <div data-id="element-801" data-mp="1jpc7egvm23uc">
      <div className="mb-8" data-id="element-802" data-mp="1jpkka8rqh95j">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-azure" data-id="element-803" data-mp="1xnilsdkyhb3m">{format(TODAY, 'EEEE, MMMM d, yyyy')}</p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-navy md:text-[28px]" data-id="element-804" data-mp="1tkm8n2e6q772">Good morning, Jordan</h1>
        <p className="mt-1.5 text-sm text-muted" data-id="element-805" data-mp="390cn5a3gmj9f">
          {pending.length > 0 ? `You have ${pending.length} time-off ${pending.length === 1 ? 'request' : 'requests'} waiting and ${outToday.length} people out today.` : `The request queue is clear. ${outToday.length} people are out today.`}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4" data-id="element-806" data-mp="32m5o5ma40e43">
        <KpiTile label="Total headcount" value={headcount} note={`Across ${departments.length} departments`} icon={UsersIcon} accent="navy" onClick={() => onNavigate({
        page: 'employees'
      })} data-id="element-807" data-mp="32oa2ky5mwvim" />
        <KpiTile label="New hires this month" value={newHires.length} note={`${started} started · ${newHires.length - started} upcoming`} icon={UserPlusIcon} accent="azure" onClick={() => onNavigate({
        page: 'employees'
      })} data-id="element-808" data-mp="3vco0ldwq3ru6" />
        <KpiTile label="Out today" value={outToday.length} note={backTomorrow ? `${backTomorrow} back tomorrow` : 'See the team calendar'} icon={PlaneIcon} accent="deep" onClick={() => onNavigate({
        page: 'timeoff',
        tab: 'whos-out'
      })} data-id="element-809" data-mp="3qi1lp0rfnh5c" />
        <KpiTile label="Pending PTO requests" value={pending.length} note={oldestPending ? `Oldest submitted ${relativeDay(oldestPending).toLowerCase()}` : 'Nothing waiting'} icon={ClockIcon} accent="flame" onClick={() => onNavigate({
        page: 'timeoff',
        tab: 'requests'
      })} data-id="element-810" data-mp="2v9rcrhoscxth" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-3" data-id="element-811" data-mp="3e2ljxpg63ln5">
        <div className="xl:col-span-2" data-id="element-812" data-mp="3938xv6x4eilm">
          <PendingRequestsCard requests={requests} onDecide={onDecide} onNavigate={onNavigate} data-id="element-813" data-mp="394jy4wrcx87z" />
        </div>
        <WhosOutCard onNavigate={onNavigate} data-id="element-814" data-mp="34axutv732gou" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3" data-id="element-815" data-mp="2zdz31jaw6ltt">
        <Card title="Upcoming anniversaries" subtitle={`${anniversaries.length} in the next 30 days`} icon={PartyPopperIcon} bodyClassName="px-3 pb-3" data-id="element-816" data-mp="nff0lfbgv1e">
          <ul className="max-h-[360px] space-y-0.5 overflow-y-auto" data-id="element-817" data-mp="3s1bg92v5xhag">
            {anniversaries.map(({
            employee,
            date,
            years
          }) => {
            const isToday = date.getTime() === TODAY.getTime();
            return <li key={employee.id} data-id="element-818" data-mp="8914sndxmw1e">
                  <button type="button" onClick={() => onNavigate({
                page: 'profile',
                employeeId: employee.id
              })} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition hover:bg-canvas/70" data-id="element-819" data-mp="d5hzk0fn08ng">
                    <Avatar name={displayName(employee)} seed={employee.id} size="sm" data-id="element-820" data-mp="r5as81aq4yy2" />
                    <span className="min-w-0 flex-1" data-id="element-821" data-mp="vwsnq9tza7bn">
                      <span className="block truncate text-sm font-medium text-navy" data-id="element-822" data-mp="vxaui0e9o3xt">{displayName(employee)}</span>
                      <span className="block truncate text-xs text-muted" data-id="element-823" data-mp="10xgfwyty0u7g">{employee.department}</span>
                    </span>
                    <span className="text-right" data-id="element-824" data-mp="1atjq8ryy7z5h">
                      <span className="block text-sm font-semibold text-navy" data-id="element-825" data-mp="1au1m3nhp8bcv">
                        {years} {years === 1 ? 'year' : 'years'}
                      </span>
                      {isToday ? <Badge tone="orange" data-id="element-826" data-mp="g7vzfxfblrgj">Today</Badge> : <span className="text-xs text-muted" data-id="element-827" data-mp="g4qaqjalsrlc">{format(date, 'MMM d')}</span>}
                    </span>
                  </button>
                </li>;
          })}
          </ul>
        </Card>

        <Card title="New hires" subtitle={`${newHires.length} this month`} icon={UserPlusIcon} bodyClassName="px-3 pb-3" data-id="element-828" data-mp="13vvsj403ttya">
          <ul className="space-y-0.5" data-id="element-829" data-mp="yxubspq5i6pz">
            {newHires.map(e => {
            const hasStarted = e.hireDate <= TODAY_ISO;
            return <li key={e.id} data-id="element-830" data-mp="1drz1aspgoqu7">
                  <button type="button" onClick={() => onNavigate({
                page: 'profile',
                employeeId: e.id
              })} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition hover:bg-canvas/70" data-id="element-831" data-mp="18uywel4clu4o">
                    <Avatar name={displayName(e)} seed={e.id} size="sm" data-id="element-832" data-mp="1q418sqyxkzmd" />
                    <span className="min-w-0 flex-1" data-id="element-833" data-mp="1v3nuzpwqlq7b">
                      <span className="block truncate text-sm font-medium text-navy" data-id="element-834" data-mp="1v2cj12t51c54">{displayName(e)}</span>
                      <span className="block truncate text-xs text-muted" data-id="element-835" data-mp="1ge85ed8xzqt7">
                        {e.jobTitle} · {e.department}
                      </span>
                    </span>
                    <Badge tone={hasStarted ? 'green' : 'blue'} dot data-id="element-836" data-mp="17da1rw2f1lb5">
                      {hasStarted ? 'Started' : 'Starts'} {formatDate(e.hireDate, 'MMM d')}
                    </Badge>
                  </button>
                </li>;
          })}
          </ul>
        </Card>

        <Card title="Headcount by department" subtitle={`${headcount} employees`} icon={BuildingIcon} className="lg:col-span-2 xl:col-span-1" data-id="element-837" data-mp="12g1g3li26z55">
          <ul className="space-y-3.5" data-id="element-838" data-mp="2rs3lppbl25m0">
            {departments.map((d, i) => <li key={d.name} data-id="element-839" data-mp="2ws83dio896dt" data-mpi={i}>
                <div className="flex items-center justify-between text-sm" data-id="element-840" data-mp="1jm51dmy85zsi">
                  <span className="text-slate-700" data-id="element-841" data-mp="1jo93q0gh6qqo">{d.name}</span>
                  <span className="font-semibold text-navy" data-id="element-842" data-mp="1flum1jojlx0z">{d.headcount}</span>
                </div>
                <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-canvas" data-id="element-843" data-mp="1asjns6xsqoko">
                  <div className={`h-full rounded-full ${i === 0 ? 'bg-flame' : i % 2 === 0 ? 'bg-azure' : 'bg-navy'}`} style={{
                width: `${d.headcount / maxDept * 100}%`
              }} data-id="element-844" data-mp="15toxyl1evzr2" />
                </div>
              </li>)}
          </ul>
        </Card>
      </div>
    </div>;
}